import { ReactNode, useEffect, useState, JSX } from 'react'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import TopBar from './TopBar'
import { SimpleTransition } from '@components/shared/SimpleTransition'
import WarningBanner from '@components/shared/WarningBanner'
import WatchlistBar from '@components/watchlist/WatchlistBar'
import { rouna, rounaDisplay, rounaMono } from 'utils/fonts'
import StatusBar from '@components/status/StatusBar'
import { useWallet } from '@solana/wallet-adapter-react'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import IntroModal, { SEEN_INTRO_KEY } from '@components/modals/IntroModal'
import { useRouter } from 'next/router'
import Footer from './Footer'
import LoadingBar from '@components/loading/LoadingBar'
import store from 'stores/store'
import ConfirmPartnerModal from '@components/partners/ConfirmPartnerModal'
import TopBanner from '@components/announcements/TopBanner'
import { useTranslation } from 'next-i18next'

export const sideBarAnimationDuration = 300

// const isValidPublicKey = (address: string) => {
//   try {
//     const publicKey = new PublicKey(address)
//     return PublicKey.isOnCurve(publicKey)
//   } catch (e) {
//     return false
//   }
// }

const Layout = ({ children }: { children: ReactNode }) => {
  const [mounted, setMounted] = useState(false)
  const routeLoading = store((s) => s.routeLoading)
  const { connecting } = useWallet()
  const router = useRouter()
  const { partner } = router.query
  const { asPath } = router
  const [showReferrerModal, setShowReferrerModal] = useState(false)
  const [seenIntroModal, setSeenIntroModal] = useLocalStorage(SEEN_INTRO_KEY)
  const [savedPartner, setSavedPartner] = useState<string | undefined>()

  useEffect(() => {
    if (partner && typeof partner === 'string') {
      setSavedPartner(partner)
    }
  }, [partner])

  useEffect(() => {
    if ((savedPartner || partner) && seenIntroModal) {
      setShowReferrerModal(true)
      setSavedPartner(undefined)
    }
  }, [savedPartner, partner, seenIntroModal])

  useEffect(() => setMounted(true), [])
  if (!mounted) return null

  const popIntroModal =
    asPath !== '/terms-of-use' &&
    asPath !== '/privacy-policy' &&
    !seenIntroModal

  return (
    <main
      className={`${rounaDisplay.variable} ${rouna.variable} ${rounaMono.variable} text-th-fgd-2 transition-all`}
    >
      <div className="mb-4">
        <TopBanner />
        <TopBar />
        <WatchlistBar />
      </div>
      <div className="mx-auto min-h-[calc(100vh-256px)] max-w-[1680px] px-4 md:px-6 lg:pt-4">
        {children}
        <WarningBanner />
      </div>
      {routeLoading || connecting ? (
        <div className="fixed top-0 z-20 h-1 w-full sm:bottom-6 sm:top-auto sm:h-0.5">
          <LoadingBar />
        </div>
      ) : null}
      <StatusBar />
      <div className={`pt-12 sm:pb-6`}>
        <Footer />
      </div>
      <DeployUpdateManager />
      {showReferrerModal ? (
        <ConfirmPartnerModal
          isOpen={showReferrerModal}
          onClose={() => setShowReferrerModal(false)}
        />
      ) : null}
      {popIntroModal ? (
        <IntroModal
          isOpen={popIntroModal}
          onClose={() => setSeenIntroModal(true)}
        />
      ) : null}
    </main>
  )
}

export default Layout

function DeployUpdateManager(): JSX.Element {
  const { t } = useTranslation('layout')
  const newBuildAvailable = store((s) => s.newBuildAvailable)

  return (
    <SimpleTransition
      show={newBuildAvailable}
      enter="transition duration-300"
      enterFrom="translate-y-0 opacity-100"
      enterTo="translate-y-[-130px] ease-in md:-translate-y-20"
      entered="translate-y-[-130px] opacity-100 md:-translate-y-20"
      leave="transition duration-300"
      leaveTo="translate-y-[-130px] opacity-0 ease-out md:-translate-y-20"
    >
      <button
        className="fixed bottom-[-46px] left-1/2 z-50 flex -translate-x-1/2 items-center rounded-full border border-th-bkg-4 bg-th-button px-4 py-3 text-th-button-text shadow-md focus:outline-none md:hover:bg-th-button-hover md:hover:shadow-none"
        onClick={() => window.location.reload()}
      >
        <p className="mr-2 whitespace-nowrap font-bold text-th-button-text">
          {t('update-available')}
        </p>
        <ArrowPathIcon className="size-5" />
      </button>
    </SimpleTransition>
  )

  // the following just doesn't work correctly (with @headless-ui/react v2.2.4 and v2.1.10):
  // - the `out` transition doesn't run because the component is removed from the DOM immediately
  // - it also doesn't work when `unmount=false` is set, the `out` transition still doesn't run
  // - it needs a deprecated `entered` prop for the `in` transition to work correctly
  // - it doesn't work at all when the `data-*` attributes are used, although it's the only method to set classes during transitions described in the official docs
  //
  // <Transition
  //   appear={true}
  //   show={newBuildAvailable}
  //   as={Fragment}
  //   enter="ease-in"
  //   enterFrom="translate-y-0"
  //   enterTo="translate-y-[-130px] md:-translate-y-20"
  //   // it is a lie that enterTo stays applied after transition end, entered is thus required to keep styles applied
  //   entered="translate-y-[-130px] md:-translate-y-20"
  //   leave="ease-out"
  //   leaveFrom="opacity-100"
  //   leaveTo="opacity-0"
  // >
  //   <button
  //     className="fixed bottom-[-46px] left-1/2 z-50 flex -translate-x-1/2 items-center rounded-full border border-th-bkg-4 bg-th-button px-4 py-3 text-th-button-text shadow-md transition duration-300 focus:outline-none md:hover:bg-th-button-hover md:hover:shadow-none"
  //     onClick={() => window.location.reload()}
  //   >
  //     <p className="mr-2 whitespace-nowrap font-bold text-th-button-text">
  //       {t('update-available')}
  //     </p>
  //     <ArrowPathIcon className="size-5" />
  //   </button>
  // </Transition>
}
